/**
 * Modelo de BOLSAS del avance lineal (flujo con rechazos re-ejecutables). Lógica
 * PURA del negocio — sin estado de componente, sin THREE. FUENTE ÚNICA: la usan
 * tanto el visor (AdvanceOverlay al pintar) como RegisterAdvanceModal (su
 * `_SimulatePools` delega aquí).
 */

/**
 * Migra un `state` de avance del formato viejo (registro plano) al nuevo
 * (segments[]). Para state ya nuevo, lo devuelve tal cual. Null si vacío.
 * @param {?Object} state
 * @returns {?Object}
 */
export function upgradeAdvanceState(state) {
  if (!state) return null
  if (Array.isArray(state.segments)) return state
  // Promoción: el state viejo tenía un único registro plano. Lo representamos
  // como un único segment.
  if (state.meters > 0) {
    return {
      direction:   state.direction || 'l2r',
      totalLength: state.totalLength || 0,
      segments: [{
        meters:      state.meters,
        status:      state.status,
        statusLabel: state.statusLabel || '',
        statusColor: state.statusColor || null,
        createdAt:   Date.now(),
      }],
    }
  }
  return null
}

/**
 * Simula el historial de un segmento (capas con rechazos) como transferencias
 * entre BOLSAS y devuelve el estado vigente:
 *   - Ejecutar (nivel 0): de (rechazado→por-iniciar) a bag[0].
 *   - Aceptar nivel k≥1: de bag[k-1] a bag[k].
 *   - Rechazar (emparejado con accept de nivel k): de bag[k-1] a `rejected`
 *     (re-ejecutable; NO resetea). Suma total = totalLength.
 * @returns {{L,positives,posLevelOfIdx,prevIdx,M,bags,unstarted,rejected,rejectedByStatus}}
 */
export function simulatePools(segments, statuses, totalLength) {
  const L = totalLength || 0
  const sts = statuses || []
  const isReject = (i) => Number(sts[i].weight) < 0
  const positives = []
  const posLevelOfIdx = {}
  const prevIdx = []
  let lastPos = -1
  for (let i = 0; i < sts.length; i++) {
    prevIdx[i] = lastPos
    if (!isReject(i)) { posLevelOfIdx[i] = positives.length; positives.push(i); lastPos = i }
  }
  const M = positives.length
  const bags = new Array(M).fill(0)
  let unstarted = L
  let rejected = 0
  // Rechazados POR ESTADO (cada estado de rechazo conserva su color/label); orden
  // de inserción = FIFO al re-ejecutar. `rejected` = total (= suma del map).
  const rejectedByStatus = new Map()
  const consumeRejected = (amount) => {
    let toReduce = amount
    for (const [sv, val] of rejectedByStatus) {
      if (toReduce <= 1e-9) break
      const r = Math.min(val, toReduce)
      rejectedByStatus.set(sv, val - r)
      toReduce -= r
    }
  }
  const valueToIdx = new Map(sts.map((s, i) => [s.value, i]))
  const segs = [...(segments || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
  for (const seg of segs) {
    const i = valueToIdx.get(seg.status)
    if (i === undefined) continue
    let m = Math.max(0, Number(seg.meters) || 0)
    if (m <= 0) continue
    if (!isReject(i)) {
      const k = posLevelOfIdx[i]
      if (k === 0) {
        const fromRej = Math.min(m, rejected); rejected -= fromRej; m -= fromRej
        if (fromRej > 0) consumeRejected(fromRej)
        const fromNew = Math.min(m, unstarted); unstarted -= fromNew; m -= fromNew
        bags[0] += (fromRej + fromNew)
      } else {
        const take = Math.min(m, bags[k - 1]); bags[k - 1] -= take; bags[k] += take
      }
    } else {
      const acceptIdx = prevIdx[i]
      const kAccept = (acceptIdx >= 0 && posLevelOfIdx[acceptIdx] !== undefined) ? posLevelOfIdx[acceptIdx] : 0
      const inputLevel = kAccept - 1
      if (inputLevel >= 0) {
        const take = Math.min(m, bags[inputLevel]); bags[inputLevel] -= take; rejected += take
        const sv = sts[i].value
        rejectedByStatus.set(sv, (rejectedByStatus.get(sv) || 0) + take)
      }
    }
  }
  return { L, positives, posLevelOfIdx, prevIdx, M, bags, unstarted, rejected, rejectedByStatus }
}

/**
 * Distribución VIGENTE de un segmento (capas con rechazos) para PINTAR, según el
 * modelo de bolsas (simulatePools). Devuelve los tramos a pintar CONCATENADOS a lo
 * largo de la polilínea: etapas más avanzadas primero (nivel alto → bajo) y al
 * final los rechazados. Lo "por iniciar" NO se incluye. Cada item:
 * {status, statusLabel, statusColor, meters}.
 */
export function currentStateSegments(segments, statuses, totalLength) {
  const pools = simulatePools(segments, statuses, totalLength)
  const eps = 1e-6
  const out = []
  // Etapas positivas con metros vigentes, de la más avanzada a la menos.
  for (let k = pools.M - 1; k >= 0; k--) {
    if ((pools.bags[k] || 0) > eps) {
      const st = statuses[pools.positives[k]]
      out.push({ status: st.value, statusLabel: st.label, statusColor: st.color, meters: pools.bags[k] })
    }
  }
  // Rechazados (re-ejecutables): un tramo por CADA estado de rechazo, con su propio
  // color/label. Orden de inserción = orden cronológico de rechazo.
  if (pools.rejectedByStatus) {
    for (const [sv, meters] of pools.rejectedByStatus) {
      if ((meters || 0) <= eps) continue
      const rej = (statuses || []).find(s => s.value === sv)
      out.push({
        status:      sv,
        statusLabel: rej ? rej.label : '',
        statusColor: (rej && rej.color) || '#e17055',
        meters,
      })
    }
  }
  return out
}
